
import { Component, Prop, Watch, Mixins } from 'vue-property-decorator'
import CustomLocationMap from '@/components/CustomLocationMap.vue'
import { Country } from '@/models/Country'
import Notification from '@/classes/Notification'
import Leaflet, { LatLng } from 'leaflet'
import { Terminal } from '@/models/Terminal'
import { GeoJSON } from 'geojson'
import Testable from '@/mixins/Testable'
import i18n from '@/i18n'

@Component({
	components: {
		CustomLocationMap
	}
})
export default class TerminalCreator extends Mixins(Testable) {
	@Prop({ required: true })
	locationCreatorOpen!: boolean

	@Prop({ default: null })
	terminal!: Terminal | null

	addressType: string = ''
	isLoading: boolean = true
	isSubmitting: boolean = false
	noMapLocationSelected: boolean = false

	city: string = ''
	terminalName: string = ''
	harbourName: string = ''
	harbourNumber: string = ''
	waterwayName: string = ''
	waterwayKmNumber: string = ''
	phoneNumber: string = ''
	email: string = ''
	comments: string = ''
	country: Country | null = null
	countries: Array<Country> = []
	geoCoordinates: string = ''
	geoCoordinatesForMap: LatLng | null = null

	async created() {
		this.isLoading = false
		await this.loadCountries()
		if (this.terminal) {
			this.city = this.terminal.place ?? ''
			this.terminalName = this.terminal.name ?? ''
			this.harbourName = this.terminal.harbour ?? ''
			this.harbourNumber = this.terminal.harbourNumberFrom ?? ''
			this.waterwayName = this.terminal.waterwayName ?? ''
			this.waterwayKmNumber = this.terminal.waterwayKmNumber ?? ''
			this.phoneNumber = this.terminal.phoneNumbers?.length ? this.terminal.phoneNumbers[0].phoneNumber : ''
			this.email = this.terminal.primaryEmail ?? ''
			this.comments = this.terminal.comments ?? ''
			this.country = this.terminal.getCountry()

			const coords = this.terminal.getLocation() as GeoJSON.Point

			if (coords) {
				this.geoCoordinates = `POINT(${coords.coordinates[0]} ${coords.coordinates[1]})`
				this.geoCoordinatesForMap = Leaflet.latLng(coords.coordinates[1], coords.coordinates[0])
			}
		}
	}

	@Watch('locationCreatorOpen')
	async loadCountries() {
		if (this.locationCreatorOpen && !this.countries.length) {
			this.isLoading = true
			this.countries = await Country.fetchAllLazy()
			this.isLoading = false
		}
	}

	get geoCoordinatesForMapPin() {
		return this.geoCoordinatesForMap
	}

	get isHarbour(): boolean {
		return this.addressType === 'harbour'
	}

	get isWaterway(): boolean {
		return this.addressType === 'waterway'
	}

	get showGeoError() {
		return this.noMapLocationSelected
	}

	emptyHarbourFields() {
		this.harbourName = ''
		this.harbourNumber = ''
	}

	emptyWaterwayFields() {
		this.waterwayName = ''
		this.waterwayKmNumber = ''
	}

	mapClicked(event) {
		this.noMapLocationSelected = false
		this.geoCoordinates = `POINT(${event.latlng.lng} ${event.latlng.lat})`
		this.geoCoordinatesForMap = event.latlng
	}

	get countryName() {
		return this.country?.name
	}

	async submit() {
		if (this.isSubmitting) {
			return
		}

		const valid = await this.$validator.validateAll()
		if (!valid || this.geoCoordinates === '') {
			this.noMapLocationSelected = this.geoCoordinates === ''
			return
		}

		this.isSubmitting = true

		const terminal = this.terminal ? this.terminal : new Terminal()
		terminal.name = this.terminalName
		terminal.place = this.city
		terminal.geoCoordinates = this.geoCoordinates
		terminal.harbour = this.harbourName
		terminal.harbourNumberFrom = this.harbourNumber
		terminal.waterwayName = this.waterwayName
		terminal.waterwayKmNumber = this.waterwayKmNumber
		terminal.phoneNumbers = [{ description: '', phoneNumber: this.phoneNumber }]
		terminal.primaryEmail = this.email
		terminal.comments = this.comments

		if (this.country) {
			terminal.setCountry(this.country)
		}

		try {
			const savedTerminal = (await terminal.save()).getModel() as Terminal
			await Notification.Success(i18n.t('terminal.created_succesfully'))
			this.$emit('terminalCreated', savedTerminal)
		} finally {
			this.isSubmitting = false
		}
	}
}
